import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration.js';
import { ComponentList } from '../../components/core/component-list.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { CalendarEntry, CalendarEntryStatus } from '../calendar-entry.js';
import { QueryableOptionalString } from '../core/optional.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { cloneDataOrNull, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { DayOfYear } from './day-of-year.js';
import { MonthOfYear } from './month-of-year.js';
import { StaffProfile } from './staff-profile.js';
import { TimeOfDay } from './time-of-day.js';
import { TimeZoneRegion } from './time-zone-region.js';

export class CalendarBreak extends CalendarEntry {
  static readonly TYPE_ID: string = 'c949b761e0e14d69b94f87fc39e90d15';
  static readonly SCHEMA_VERSION: number = 6;

  public readonly dayId: string;
  public readonly monthId: string;

  private constructor(
    public readonly uniqueId: string,
    public readonly status: CalendarEntryStatus,
    public readonly creatorId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly dayOfYear: DayOfYear,
    public readonly timeOfDay: TimeOfDay,
    public readonly timezone: TimeZoneRegion,
    public readonly creationDate: dayjs.Dayjs,
    public readonly staffMemberId: QueryableOptionalString,
    public readonly staffMember: StaffProfile | null,
    public readonly components: ComponentList,
    public readonly duration: Duration,
    public readonly cancelledDate: dayjs.Dayjs | null,
    public readonly cancelledBy: string | null,
  ) {
    super();
    this.dayId = dayOfYear.uniqueId;
    this.monthId = MonthOfYear.dayOfYearToUniqueId(dayOfYear);
  }

  get groupId(): string | null {
    return null;
  }

  get parentId(): QueryableOptionalString {
    return QueryableOptionalString.none;
  }

  get $typeId(): string {
    return CalendarBreak.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(4, CalendarBreak.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CalendarBreak {
    return new CalendarBreak(
        MapUtils.get<string>(map, 'uniqueId'),
        toEnumFromString(MapUtils.get<string>(map, '$status'), CalendarEntryStatus),
        MapUtils.get<string>(map, 'creatorId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.fromData<DayOfYear>(MapUtils.get<SerializedData>(map, 'dayOfYear')),
        context.fromData<TimeOfDay>(MapUtils.get<SerializedData>(map, 'timeOfDay')),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        new QueryableOptionalString(MapUtils.getOrNull<string>(map, 'staffMemberId')),
        context.fromDataOrNull<StaffProfile>(MapUtils.getOrNull<SerializedData>(map, 'staffMember')),
        ComponentList.deserialize(context, MapUtils.get<SerializedData[]>(map, 'components')),
        context.decodeDuration(MapUtils.get<number>(map, 'duration')),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'cancelledDate')),
        MapUtils.getOrNull<string>(map, 'cancelledBy'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      '$status': fromEnumAsString(this.status),
      'creatorId': this.creatorId,
      'businessRef': this.businessRef.encode(),
      'dayId': this.dayId,
      'monthId': this.monthId,
      'dayOfYear': context.toData(this.dayOfYear),
      'timeOfDay': context.toData(this.timeOfDay),
      'duration': context.encodeDuration(this.duration),
      'timezone': fromEnumAsString(this.timezone),
      'creationDate': context.encodeDate(this.creationDate),
      'staffMemberId': this.staffMemberId.queryableValue,
      'staffMember': context.toDataOrNull(this.staffMember),
      'date': context.encodeDate(this.time),
      'components': this.components.serialize(context),
      'groupId': this.groupId,
      'parentId': this.parentId.queryableValue,
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
    };
  }

  clone(): this {
    return new CalendarBreak(
        this.uniqueId,
        this.status,
        this.creatorId,
        this.businessRef.clone(),
        this.dayOfYear.clone(),
        this.timeOfDay.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.staffMemberId.clone(),
        cloneDataOrNull(this.staffMember),
        this.components.clone(),
        TimeZone.cloneDuration(this.duration),
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
            other.status === this.status &&
            other.creatorId === this.creatorId &&
            other.cancelledDate === this.cancelledDate &&
            other.cancelledBy === this.cancelledBy &&
            isSameData(other.businessRef, this.businessRef) &&
            isSameData(other.dayOfYear, this.dayOfYear) &&
            isSameData(other.timeOfDay, this.timeOfDay) &&
            other.timezone === this.timezone &&
            other.creationDate === this.creationDate &&
            isSameData(other.staffMemberId, this.staffMemberId) &&
            isSameData(other.staffMember, this.staffMember) &&
            isSameData(other.components, this.components) &&
            other.duration === this.duration;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new CalendarBreak(
        this.uniqueId,
        this.status,
        this.creatorId,
        businessRef.clone(),
        this.dayOfYear.clone(),
        this.timeOfDay.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.staffMemberId.clone(),
        this.staffMember?.copyWithBusinessRef(businessRef) ?? null,
        this.components.copyWithBusinessRef(businessRef),
        TimeZone.cloneDuration(this.duration),
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }
}

export class CalendarBreakBuilder extends DataModelBuilder<CalendarBreak> {
  build(context: SerializationContext, map: SerializedData): CalendarBreak {
    return CalendarBreak.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    MapUtils.replace(
        map, '$status', 'DELETED',
        fromEnumAsString(CalendarEntryStatus.Canceled),
    );

    if(versionRange.contains(CalendarBreak.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CalendarBreak.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version components where added to the model
    if(version < 2) {
      MapUtils.set(map, 'components', []);
      version = 2;
    }

    // In this version the staff member and staff member id where made [Optional]
    if(version < 3) {
      version = 3;
    }

    // In this version [businessId] was replaced with [businessRef]
    if(version < 4) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 4;
    }

    // In this version the [dayId] field was removed from the constructor
    if(version < 5) {
      version = 5;
    }

    // In this version the [cancelledDate] and [cancelledBy] fields were added
    if(version < 6) {
      version = 6;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
